'use client'

import { useEffect, useCallback, useRef, useState } from "react";
import { useStateStore } from "@/context/stateStore";
import MainTemplate from "../templates/mainTemplate";
import ModalProvider from "../providers/modalProvider";
import { useSession } from "next-auth/react";
import { InitializeData } from "@/utils/userHelpers/initData";
import { LoadingSpinner } from "@/components/misc/loadingSpinner";
import NavWrapper from "./navWrapper";
import { Overlay } from "@mantine/core";
import VerifyUser from "@/utils/userHelpers/verifyUser";

export default function PageWrapper({ children }: Readonly<{ children: React.ReactNode; }>) {

    const [loading, setLoading] = useState(true);
    const { data: session, update, status } = useSession();
    const userEmail = session?.user?.email ?? '';
    const widthRef = useRef<number>(0);
    const heightRef = useRef<number>(0);
    const setWidthQuery = useStateStore((state) => state.setWidthQuery);
    const setHeightQuery = useStateStore((state) => state.setHeightQuery);
    const urlToUse = process.env.NEXT_PUBLIC_BASE_URL ? process.env.NEXT_PUBLIC_BASE_URL : '';
    const globalLoading = useStateStore(state => state.globalLoading);
    const dataLoading = useStateStore(state => state.dataLoading);
    const dataHydrated = useStateStore(state => state.dataHydrated);
    const dataError = useStateStore(state => state.dataError);
    const clearAppData = useStateStore(state => state.clearAppData);

    const initializeSizes = useCallback(() => {
        setWidthQuery(widthRef.current);
        setHeightQuery(window.innerHeight);
    }, [setWidthQuery, setHeightQuery]);

    const handleUpdate = async () => {
        await update();
    }

    const updateMedia = useCallback(() => {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        if (newWidth !== widthRef.current) {
            widthRef.current = newWidth;
            setWidthQuery(newWidth);
        }
        if (newHeight !== heightRef.current) {
            heightRef.current = newHeight;
            setHeightQuery(newHeight);
        }
    }, [setWidthQuery, setHeightQuery]);

    useEffect(() => {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;
        if (!newWidth || !newHeight) {
            return;
        }
        widthRef.current = newWidth
        heightRef.current = newHeight;
        initializeSizes();

        window.addEventListener('resize', updateMedia);
        return () => window.removeEventListener('resize', updateMedia);
    }, [updateMedia, initializeSizes]);

    

    useEffect(() => {
        const hydrateApp = async () => {
            if (status === 'loading') {
                return;
            }

            if (status === 'unauthenticated' || !userEmail) {
                clearAppData();
                setLoading(false);
                return;
            }

            setLoading(true);
            await VerifyUser({ userEmail, urlToUse });
            await InitializeData({ urlToUse, userEmail });
            setLoading(false);
        }

        hydrateApp();
    }, [clearAppData, status, urlToUse, userEmail]);

    return (
        <div className="flex flex-col justify-start items-center w-screen h-dvh overflow-hidden">
            {status === 'loading' ? (
                <LoadingSpinner />
            ) : (
                <>
                    <NavWrapper />
                    <ModalProvider handleUpdate={handleUpdate} session={session} />
                    <MainTemplate>
                        {loading || (status === 'authenticated' && dataLoading && !dataHydrated) ? (
                            <LoadingSpinner label="Loading your garden" />
                        ) : dataError ? (
                            <div className="flex h-full w-full items-center justify-center p-6">
                                <div className="w-full max-w-md rounded-md border border-red-200 bg-white p-5 text-center shadow-sm">
                                    <p className="text-sm font-semibold text-red-700">{dataError}</p>
                                    <p className="mt-2 text-xs text-gray-600">Refresh the page or sign in again if the problem keeps happening.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {children}
                            </>
                        )}
                    </MainTemplate>

                    {(globalLoading || (dataLoading && dataHydrated)) && (
                        <Overlay
                            gradient="linear-gradient(145deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%)"
                            opacity={0.85}
                        >
                            <LoadingSpinner label="Refreshing" compact />
                        </Overlay>
                    )}
                </>
            )}
        </div>
    )
}
